// The only place this module talks to the app journey API (Cloud Run, Ketosource/app-journey-api, C3).
// The Pages Function proxy (functions/app-journey/) strips the portal prefix and forwards the Access JWT;
// the API verifies it, asks the portal for module:app_journey, and serves one production release's
// captured screens. Every figure and label on screen is the API's: this module formats, never computes (I-4).

export type FreshnessMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
};

/** S8 `meta` plus the release the API names on every 200. */
export type JourneyMeta = Partial<FreshnessMeta> & { release?: string };

export type Envelope<T> = { data: T; meta: JourneyMeta };

export type Canvas = {
  design?: string | null;
  version?: string | null;
  canvas_head?: string | null;
  version_off?: boolean | null;
};

export type CapturedPath = {
  name: string;
  flow: string;
  vars: Record<string, string>;
  signature: string;
  ok: boolean;
  screens?: number;
  notes?: string[];
};

export type Current = {
  release: string;
  commit: string | null;
  build: string | null;
  bundle_md5: string | null;
  captured_at: string | null;
  release_url: string | null;
  base_kind: string | null;
  canvas: Canvas | null;
  canvas_pairing_gaps: unknown[] | null;
  paths: CapturedPath[];
};

export type Flow = {
  title: string;
  start_frame: string | null;
  pending: string | null;
  variables: Record<string, string[]>;
  captured: Array<{ name: string; vars: Record<string, string>; ok: boolean }>;
};

export type Flows = { release: string; flows: Record<string, Flow>; derived: boolean };

export type Step = {
  n: number;
  route: string;
  page: string | null;
  frame_id: string | null;
  frame_title: string | null;
  png_url: string;
  tap: string | null;
  actions: string[];
  labels: string[];
  step_id: string | null;
  /** The funnel-stats row for this step, passed through unchanged; never read as a number here. */
  figures: Record<string, unknown> | null;
};

export type Drift =
  | { kind: "pairing_gaps"; gaps: unknown[] }
  | { kind: "version_off" }
  | { kind: "published_unknown" }
  | { kind: "canvas_version"; captured: string; published: string };

export type Sequence = {
  release: string;
  commit: string | null;
  bundle_md5: string | null;
  flow: string;
  variables: Record<string, string>;
  name: string;
  signature: string;
  ok: boolean;
  notes: string[];
  labels_withheld: boolean;
  steps: Step[];
  labelled_against: { design?: string | null; version?: string | null; head?: string | null } | null;
  published_now: { version: string | null; head: string | null } | null;
  drift: Drift[];
  overlay: { status: string; [key: string]: unknown } | null;
};

export type GraphNode = { route: string; page: string | null; frame_id: string | null; frame_title: string | null };
export type GraphEdge = { from: string; tap: string | null; to: string; paths: string[] };
export type Graph = { release: string; nodes: GraphNode[]; edges: GraphEdge[] };

/** The portal path the proxy serves; `png_url` from the API is relative to the service. */
export const BASE = "/app-journey";

export class JourneyError extends Error {
  constructor(
    public status: number,
    public key: string | null,
    message: string,
    public details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function get<T>(path: string): Promise<Envelope<T>> {
  const r = await fetch(`${BASE}${path}`, { credentials: "include", headers: { Accept: "application/json" } });
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    const key = typeof j.error === "string" ? j.error : null;
    const message = typeof j.message === "string" ? j.message : `HTTP ${r.status}`;
    const details = j.details && typeof j.details === "object" ? (j.details as Record<string, unknown>) : {};
    throw new JourneyError(r.status, key, message, details);
  }
  return (await r.json()) as Envelope<T>;
}

export const currentKey = ["app_journey", "current"] as const;
export const flowsKey = ["app_journey", "flows"] as const;
export const graphKey = ["app_journey", "graph"] as const;
export const sequenceKey = (flow: string, vars: Record<string, string>) =>
  ["app_journey", "sequence", flow, sequenceVars(vars)] as const;

export const getCurrent = () => get<Current>("/v1/journey/current");
export const getFlows = () => get<Flows>("/v1/journey/flows");
export const getGraph = () => get<Graph>("/v1/journey/graph");
export const getSequence = (flow: string, vars: Record<string, string>) =>
  get<Sequence>(`/v1/journey/sequence?flow=${encodeURIComponent(flow)}&vars=${encodeURIComponent(sequenceVars(vars))}`);

/** Keys sorted so the same answers are one cache entry and one URL, whatever order they were picked in. */
export function sequenceVars(vars: Record<string, string>): string {
  return JSON.stringify(Object.fromEntries(Object.entries(vars).sort(([a], [b]) => a.localeCompare(b))));
}

export function screenUrl(pngUrl: string): string {
  return `${BASE}${pngUrl}`;
}

/** The freshness line: capture time from the API's own `meta`; the release is shown by the module header. */
export function freshness(meta: JourneyMeta | undefined): FreshnessMeta | null | undefined {
  if (!meta) return undefined;
  if (!meta.data_as_of) return null;
  return {
    data_as_of: meta.data_as_of,
    source: meta.source ?? null,
    expected_max_age_hours: meta.expected_max_age_hours ?? null,
  };
}
